/**
 * GameLeaderboardSheet — the dark bottom-sheet leaderboard opened from the warmup hub header.
 *
 * One tab per score-based game (registry-driven; one game today). Shows the top-3 podium + ranked
 * list for the selected game and a pinned "You" row with the viewer's standing. Data + ranking are
 * server-authoritative; all-time best only (no timeframes in v1).
 *
 * Split into a presentational `GameLeaderboardSheetView` (states driven by props — story/test friendly)
 * and a thin `GameLeaderboardSheet` container that wires the React Query hook + tab state.
 */

import { useState } from 'react';
import { View, Text, Pressable, ScrollView, ActivityIndicator, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Svg, Path } from 'react-native-svg';
import { BaseModal } from '../../ui/BaseModal/BaseModal';
import { FriendAvatar } from '../../friends/shared/FriendAvatar';
import { GameLeaderboardPodium } from './GameLeaderboardPodium';
import { GameLeaderboardRow } from './GameLeaderboardRow';
import {
  gameLeaderboardSheetStyles as styles,
  sheetHeightStyle,
  youBarSafeAreaStyle,
  SHEET_TOP_GAP_MIN,
} from './GameLeaderboardSheet.styles';
import {
  GAME_LB_COLORS,
  GAME_LB_ROW_AVATAR_SIZE,
  GAME_LB_TEST_IDS,
} from './GameLeaderboardSheet.constants';
import { LEADERBOARD_GAMES } from '../../../src/quiz/constants/gameLeaderboards.constants';
import type { LeaderboardGame } from '../../../src/quiz/constants/gameLeaderboards.constants';
import { useGameLeaderboard } from '../../../src/quiz/hooks/useGameLeaderboard';
import type { GameLeaderboardData } from '../../../src/quiz/helpers/gamesApi';

const FIRST_GAME = LEADERBOARD_GAMES[0]?.gameKey ?? null;

function TrophyGlyph() {
  return (
    <Svg width={22} height={22} viewBox="0 0 24 24" fill="none">
      <Path d="M8 4h8v3a4 4 0 01-8 0V4z" fill={GAME_LB_COLORS.crown} />
      <Path
        d="M8 5H5v1.5A2.8 2.8 0 008 9M16 5h3v1.5A2.8 2.8 0 0116 9"
        stroke={GAME_LB_COLORS.crown}
        strokeWidth={1.5}
      />
      <Path d="M12 11v3M9 19h6l-.7-3.4H9.7L9 19z" fill={GAME_LB_COLORS.crown} />
    </Svg>
  );
}

function CloseGlyph() {
  return (
    <Svg width={13} height={13} viewBox="0 0 13 13" fill="none">
      <Path d="M1 1l11 11M12 1L1 12" stroke={GAME_LB_COLORS.closeIcon} strokeWidth={1.8} strokeLinecap="round" />
    </Svg>
  );
}

/** "Top X%" once ranked; an invite to play otherwise. */
function youContext(data: GameLeaderboardData | undefined): string {
  const rank = data?.currentUser.rank;
  if (!rank || !data || data.total <= 0) return 'Set a score to join';
  const pct = Math.max(1, Math.round((rank / data.total) * 100));
  return `Top ${pct}%`;
}

interface GameLeaderboardSheetViewProps {
  visible: boolean;
  onClose: () => void;
  games: readonly LeaderboardGame[];
  activeGameKey: string | null;
  onSelectGame: (gameKey: string) => void;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  data?: GameLeaderboardData;
}

export function GameLeaderboardSheetView({
  visible,
  onClose,
  games,
  activeGameKey,
  onSelectGame,
  isLoading,
  isError,
  onRetry,
  data,
}: GameLeaderboardSheetViewProps) {
  const insets = useSafeAreaInsets();
  const { height: windowHeight } = useWindowDimensions();
  // Explicit height = full screen minus a top gap (>= the notch), bottom-anchored so it covers the
  // navbar and never overflows above on small screens. The "You" bar pads for the home indicator.
  const topGap = Math.max(insets.top, SHEET_TOP_GAP_MIN);
  const sheetHeight = Math.max(0, windowHeight - topGap);

  const activeGame = games.find((g) => g.gameKey === activeGameKey) ?? games[0];

  const entries = data?.entries ?? [];
  const top3 = entries.slice(0, 3);
  const rest = entries.slice(3);
  const youRank = data?.currentUser.rank ?? null;
  const youScore = data?.currentUser.score ?? 0;

  const renderBody = () => {
    if (isLoading) {
      return (
        <View style={styles.stateBox} testID={GAME_LB_TEST_IDS.loading}>
          <ActivityIndicator color={GAME_LB_COLORS.tabSelectedBg} />
        </View>
      );
    }
    if (isError) {
      return (
        <View style={styles.stateBox} testID={GAME_LB_TEST_IDS.error}>
          <Text style={styles.stateText}>Couldn&apos;t load the leaderboard.</Text>
          <Pressable
            onPress={onRetry}
            style={({ pressed }) => [styles.retryButton, pressed && styles.pressed]}
            testID={GAME_LB_TEST_IDS.retry}
          >
            <Text style={styles.retryText}>Retry</Text>
          </Pressable>
        </View>
      );
    }
    if (entries.length === 0) {
      return (
        <View style={styles.stateBox} testID={GAME_LB_TEST_IDS.empty}>
          <Text style={styles.stateText}>Be the first to set a score.</Text>
        </View>
      );
    }
    return (
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <GameLeaderboardPodium top3={top3} />
        <View style={styles.list}>
          {rest.map((entry) => (
            <GameLeaderboardRow key={entry.userId} entry={entry} />
          ))}
        </View>
      </ScrollView>
    );
  };

  return (
    <BaseModal
      isVisible={visible}
      onDismiss={onClose}
      contentAlignment="flex-end"
      contentContainerStyle={styles.modalContent}
      animationType="slide"
      blurIntensity={10}
      testID={GAME_LB_TEST_IDS.sheet}
      accessibilityLabel="Leaderboard"
    >
      <View style={[styles.sheet, sheetHeightStyle(sheetHeight)]}>
        <View style={styles.handle} />

        <View style={styles.headerRow}>
          <View style={styles.titleRow}>
            <TrophyGlyph />
            <Text style={styles.title}>Leaderboard</Text>
          </View>
          <Pressable
            onPress={onClose}
            style={({ pressed }) => [styles.closeButton, pressed && styles.pressed]}
            accessibilityRole="button"
            accessibilityLabel="Close leaderboard"
            testID={GAME_LB_TEST_IDS.close}
          >
            <CloseGlyph />
          </Pressable>
        </View>

        <View style={styles.tabRow}>
          {games.map((game) => {
            const selected = game.gameKey === activeGame?.gameKey;
            return (
              <Pressable
                key={game.gameKey}
                onPress={() => onSelectGame(game.gameKey)}
                style={[styles.tab, selected ? styles.tabSelected : styles.tabIdle]}
                accessibilityRole="tab"
                accessibilityState={{ selected }}
                testID={GAME_LB_TEST_IDS.tab(game.gameKey)}
              >
                <Text style={selected ? styles.tabTextSelected : styles.tabTextIdle}>{game.label}</Text>
              </Pressable>
            );
          })}
        </View>

        <View style={styles.body}>{renderBody()}</View>

        <View style={[styles.youBar, youBarSafeAreaStyle(insets.bottom)]}>
          <View style={styles.youInner} testID={GAME_LB_TEST_IDS.youRow}>
            <Text style={styles.youRank}>{youRank ?? '—'}</Text>
            <FriendAvatar size={GAME_LB_ROW_AVATAR_SIZE} nameForInitials="You" />
            <View style={styles.youTextCol}>
              <Text style={styles.youName}>You</Text>
              <Text style={styles.youContext}>{youContext(data)}</Text>
            </View>
            <Text style={styles.youScore}>{youScore}</Text>
          </View>
        </View>
      </View>
    </BaseModal>
  );
}

interface GameLeaderboardSheetProps {
  visible: boolean;
  onClose: () => void;
  /** When a tab is selected, the parent may track analytics (game_key). */
  onSelectGame?: (gameKey: string) => void;
}

export function GameLeaderboardSheet({ visible, onClose, onSelectGame }: GameLeaderboardSheetProps) {
  const [gameKey, setGameKey] = useState<string | null>(FIRST_GAME);
  const query = useGameLeaderboard(gameKey, visible);

  const handleSelectGame = (key: string) => {
    setGameKey(key);
    onSelectGame?.(key);
  };

  return (
    <GameLeaderboardSheetView
      visible={visible}
      onClose={onClose}
      games={LEADERBOARD_GAMES}
      activeGameKey={gameKey}
      onSelectGame={handleSelectGame}
      isLoading={query.isLoading}
      isError={query.isError}
      onRetry={() => query.refetch()}
      data={query.data}
    />
  );
}
